import type { ReactNode } from "react";
import clsx from "clsx";
import { PageHeader } from "../PageHeader/PageHeader.js";
import { useMinWidth } from "../hooks/useBreakpoint.js";
import styles from "./Page.module.scss";

export interface PageProps {
  /**
   * The page title. When set, the page renders a `<PageHeader>` (title left, `actions` right) as its
   * first child. Omit it for a page with no header — a dashboard, a hero, `Booking` — which simply does
   * not pass one and gets the same rhythm with nothing at the top.
   */
  title?: ReactNode;
  /** An optional one-line description under the title. Ignored without a `title`. */
  subtitle?: ReactNode;
  /** Right-aligned header actions (a toggle, a button). Ignored without a `title`. */
  actions?: ReactNode;
  /** id for the h1, when something needs `aria-labelledby` pointed at it. */
  titleId?: string;
  /** Wrapper element — `section` when the page is a labelled region, else `div` (the default). */
  as?: "div" | "section";
  /**
   * Page-specific layout ON the wrapper (a grid the body needs, a rail). The vertical rhythm is owned by
   * `Page` regardless, so a page never re-declares gap or the gutter — that duplication is what let the
   * spacing above/below the header drift page-to-page.
   */
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  children: ReactNode;
}

/**
 * The standard content-page wrapper: one flex column, one gap, one mobile-gated gutter — the rhythm that
 * every shell content page shares, defined ONCE so it cannot diverge. It optionally renders the page's
 * header (`title` + `actions`), so "every page has an optional header that takes a label and actions" is a
 * property of the wrapper, not something each page hand-rolls above its own body.
 *
 * It is deliberately NOT for bespoke layouts — the dashboard hero, the roster shell, the Buses fixed-rail
 * page, a dialog's inner `.page`, or the mobile-only screens — which own their own frame. `page-shell.guard`
 * keeps the standard pages on this and allowlists the bespoke ones by name.
 */
export function Page({ title, subtitle, actions, titleId, as: As = "div", className, children, ...rest }: PageProps) {
  // Below `lg` the mobile shell carries the page title in its own header (beside the logo), so the page does
  // NOT render its own — it would double. Actions still render (in a headerless right-aligned row) so a
  // page's top-right control survives on the phone. Desktop (≥ lg, no shell header title) renders the full
  // header. `Page` is a core-app primitive (Antenna does not use it) and the mobile shell mounts exactly
  // below lg, so this suppression matches the shell 1:1. See `mobileSectionTitle` for the header side.
  const desktop = useMinWidth("lg");
  return (
    <As className={clsx(styles.page, className)} {...rest}>
      {title != null &&
        (desktop ? (
          <PageHeader title={title} id={titleId} subtitle={subtitle} actions={actions} />
        ) : (
          actions != null && <div className={styles.mobileActions}>{actions}</div>
        ))}
      {children}
    </As>
  );
}
